import { call, put, select, SelectEffect, CallEffect, PutEffect } from 'redux-saga/effects';

import { createElement, CreateTextElementAction } from 'js/actionCreators/scribeActions';
import { DEFAULT_TEXT_LINE_HEIGHT, defaultTextStylingConfig, MAX_CHARS_IN_TEXT } from 'js/config/defaults';
import { DEFAULT_TEXT_ELEMENT_COPY, SCOPE } from 'js/config/consts';
import ScribeTextElementModel from 'js/models/ScribeTextElementModel';
import { getTextDimensions } from 'js/shared/helpers/text-draw';
import { updateBitmapFonts } from 'js/actionCreators/fontActions';
import { ExistingScribeModel, NewScribeTextElementProps, RootState } from 'js/types';
import { BrandKitFontsState } from 'js/reducers/brandKitFonts';
import { UserWithPermissions } from 'js/permissions/permissionsHelpers';

import { getScribeById } from '../selectors';
import { yieldUserWithPermission } from '../sagaHelpers/yieldUserWithPermission';

export function* createTextElement(
  action: CreateTextElementAction
): Generator<
  SelectEffect | CallEffect | PutEffect,
  void,
  (ExistingScribeModel | undefined) | BrandKitFontsState | UserWithPermissions
> {
  const { scribeId, initialText } = action;
  const scribe = (yield select(getScribeById, scribeId)) as ExistingScribeModel | undefined;

  if (!scribe) return;

  const scribeTextSettings = scribe.settings?.textStylingConfig ?? defaultTextStylingConfig;
  const text = initialText?.substring(0, MAX_CHARS_IN_TEXT) || DEFAULT_TEXT_ELEMENT_COPY;

  const { useBrandKitFonts, styles } = (yield select((s: RootState) => s.brandKitFonts)) as BrandKitFontsState;
  const user = (yield call(yieldUserWithPermission)) as UserWithPermissions;

  const shouldUseBrandKit = useBrandKitFonts && user.can([SCOPE.useBrandKit]) && styles.length > 0;
  const defaultBrandStyle = shouldUseBrandKit ? (styles.find(s => s.isDefault) ?? styles[0]) : null;

  const styleToBeApplied = defaultBrandStyle
    ? {
        align: defaultBrandStyle.align,
        font: defaultBrandStyle.font,
        fontWeight: defaultBrandStyle.bold ? 'bold' : 'normal',
        fontStyle: defaultBrandStyle.italic ? 'italic' : 'normal',
        fill: defaultBrandStyle.colorHex,
        opacity: scribeTextSettings.opacity,
        fontSize: defaultBrandStyle.fontSize,
        lineHeight: defaultBrandStyle.lineSpacing
      }
    : {
        align: scribeTextSettings.align,
        font: scribeTextSettings.font,
        fontWeight: scribeTextSettings.fontWeight,
        fontStyle: scribeTextSettings.fontStyle,
        fill: scribeTextSettings.fill,
        opacity: scribeTextSettings.opacity,
        fontSize: scribeTextSettings.fontSize,
        lineHeight: scribeTextSettings.lineHeight ?? DEFAULT_TEXT_LINE_HEIGHT
      };

  const { height, width } = getTextDimensions({
    text,
    font: styleToBeApplied.font,
    fontSize: styleToBeApplied.fontSize,
    lineHeight: styleToBeApplied.lineHeight,
    fontStyle: styleToBeApplied.fontStyle,
    fontWeight: styleToBeApplied.fontWeight,
    align: styleToBeApplied.align,
    fill: styleToBeApplied.fill
  });

  const elem = new ScribeTextElementModel({
    text,
    width,
    height,
    scaleX: 1,
    scaleY: 1,
    ...styleToBeApplied
  } as NewScribeTextElementProps);

  yield put(createElement(elem, scribeId));

  const existingTextElements = scribe.elements.filter(element => element.type === 'Text');
  yield put(updateBitmapFonts([...existingTextElements, elem]));
}
